import { createApi } from '@reduxjs/toolkit/query/react';
import { IPagedResult } from '../../shared/models/paged-result';
import { IAttributeType } from '../../shared/models/product/attribute-type';
import { IAttributeTypeFilterSet } from '../../shared/models/product/attribute-type-filter-set';
import { IBrand } from '../../shared/models/product/brand';
import { IBrandFilterSet } from '../../shared/models/product/brand-filter-set';
import { IModel } from '../../shared/models/product/model';
import { IModelFilterSet } from '../../shared/models/product/model-filter-set';
import { IProductCatalog } from '../../shared/models/product/product-catalog';
import { IProductCatalogFilterSet } from '../../shared/models/product/product-catalog-filter-set';
import { IProductCatalogListItem } from '../../shared/models/product/product-catalog-list-item';
import { IVehicleType } from '../../shared/models/product/vehicle-type';
import { IVehicleTypeFilterSet } from '../../shared/models/product/vehicle-type-filter-set';
import { IVehicleFilterSet } from '../../shared/models/product/vehicle-filter-set';
import { serialize } from '../../utility/serialize';
import { baseQuery } from './baseQuery';
import { IBulkUploadPayload, IBulkUploadResponse } from '../../shared/models/product/bulk-upload';
import { IDiscountPriceFilterSet } from '../../shared/models/product/discount-price-filter-set';
import { IDiscountPrice } from '../../shared/models/product/discount-price';
import { downloadBlob } from '../../utility/file';
import { IRetireProductDetail } from '../../shared/models/product/retire-product-detail';
import { IWithdrawReason } from '../../shared/models/product/withdraw-reason';
import {
  IAsiriVehicleSearchRequest,
  IAsiriVehicleSearchResult,
  IAsiriVehicleSyncRequest,
  IAsiriVehicleSyncResponse,
} from '../../shared/models/product/asiri-vehicle-vin';

interface IProductCatalogTemplateRequest {
  companyId: string;
}

interface IProductCatalogTemplateDownloadResult {
  success: true;
  fileName: string;
}

const PRODUCT_CATALOG_TEMPLATE_FILE_NAME = 'PlantillaCargaCatalogo.xlsx';

const getFileNameFromContentDisposition = (contentDisposition: string | null, fallback: string) => {
  if (!contentDisposition) return fallback;

  const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  const fileNameMatch = contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
  const encodedFileName = utf8FileNameMatch?.[1] ?? fileNameMatch?.[1];

  if (!encodedFileName) return fallback;

  try {
    return decodeURIComponent(encodedFileName.trim());
  } catch {
    return encodedFileName.trim();
  }
};

const productCatalogApi = createApi({
  reducerPath: 'productCatalogApi',
  baseQuery,
  tagTypes: ['ProductCatalog'],
  endpoints: (builder) => ({
    getProductCatalogs: builder.query<
      IPagedResult<IProductCatalogListItem>,
      IProductCatalogFilterSet | string | void
    >({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '{"page":"1","size":"50"}';
        return {
          url: `/api/productcatalogs${qs}`,
          method: 'GET',
        };
      },
    }),
    getProductCatalog: builder.query<IProductCatalog, string>({
      query: (id) => ({
        url: `/api/productcatalogs/${id}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      providesTags: ['ProductCatalog'],
    }),
    createProductCatalog: builder.mutation<IProductCatalogListItem, IProductCatalog>({
      query: (body) => ({
        url: '/api/productcatalogs',
        method: 'POST',
        body,
      }),
    }),
    updateProductCatalog: builder.mutation<IProductCatalogListItem, IProductCatalog>({
      query: (body) => ({
        url: `/api/productcatalogs/${body.id}`,
        method: 'PUT',
        body,
      }),
    }),
    deleteProductCatalog: builder.mutation<boolean, string>({
      query: (id) => ({
        url: `/api/productcatalogs/${id}`,
        method: 'DELETE',
      }),
    }),
    listAttributeTypes: builder.query<IAttributeType[], IAttributeTypeFilterSet | string | void>({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '';
        return {
          url: `/api/productcatalogs/attributetype${qs}`,
          method: 'GET',
        };
      },
    }),
    listVehicleTypes: builder.query<IVehicleType[], IVehicleTypeFilterSet | string | void>({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '';
        return {
          url: `/api/productcatalogs/vehicletype${qs}`,
          method: 'GET',
        };
      },
    }),
    listBrands: builder.query<IBrand[], IBrandFilterSet | string | void>({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '';
        return {
          url: `/api/productcatalogs/brand${qs}`,
          method: 'GET',
        };
      },
    }),
    listModels: builder.query<IModel[], IModelFilterSet | string | void>({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '';
        return {
          url: `/api/productcatalogs/model${qs}`,
          method: 'GET',
        };
      },
    }),
    getProductWithSkus: builder.query<IProductCatalog[], IProductCatalogFilterSet>({
      query: (filterSet) => {
        const qs = filterSet ? `${serialize(filterSet)}` : '';
        return {
          url: `/api/productcatalogs/list?${qs}`,
          method: 'GET',
        };
      },
    }),
    getVehicleModels: builder.query<IModel[], IVehicleFilterSet | string | void>({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '';
        return {
          url: `/api/productcatalogs/vehicle/models${qs}`,
          method: 'GET',
        };
      },
    }),
    getVehicleColors: builder.query<IAttributeType[], IVehicleFilterSet | string | void>({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '';
        return {
          url: `/api/productcatalogs/vehicle/colors${qs}`,
          method: 'GET',
        };
      },
    }),
    getVehicleYears: builder.query<number[], IVehicleFilterSet | string | void>({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '';
        return {
          url: `/api/productcatalogs/vehicle/manufacture-years${qs}`,
          method: 'GET',
        };
      },
    }),
    downloadProductCatalogTemplate: builder.mutation<
      IProductCatalogTemplateDownloadResult,
      IProductCatalogTemplateRequest
    >({
      async queryFn(args, _api, _extraOptions, fetchWithBQ) {
        const params = new URLSearchParams({ companyId: args.companyId });
        const result = await fetchWithBQ({
          url: `/api/productCatalogs/template-catalog?${params.toString()}`,
          method: 'GET',
          responseHandler: async (response) => {
            if (!response.ok) {
              return response.text();
            }

            return {
              blob: await response.blob(),
              contentDisposition: response.headers.get('content-disposition'),
            };
          },
          validateStatus: (response) => response.ok,
        });

        if (result.error) {
          return { error: result.error };
        }

        const data = result.data as {
          blob: Blob;
          contentDisposition: string | null;
        };
        const fileName = getFileNameFromContentDisposition(
          data.contentDisposition,
          PRODUCT_CATALOG_TEMPLATE_FILE_NAME
        );

        downloadBlob(data.blob, fileName);

        return {
          data: {
            success: true,
            fileName,
          },
        };
      },
    }),
    bulkUpload: builder.mutation<IBulkUploadResponse, IBulkUploadPayload>({
      query: (body) => ({
        url: '/api/productcatalogs/bulk-upload',
        method: 'POST',
        body,
      }),
    }),
    getDiscountPrice: builder.query<IDiscountPrice, IDiscountPriceFilterSet>({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '';
        return {
          url: `/api/productcatalogs/discount-price${qs}`,
          method: 'GET',
        };
      },
    }),
    listWithdrawReasons: builder.query<IWithdrawReason[], void>({
      query: () => ({
        url: `/api/productcatalogs/withdraw-reasons`,
        method: 'GET',
      }),
    }),
    retireProductDetail: builder.mutation<void, IRetireProductDetail>({
      query: (body) => ({
        url: '/api/productcatalogs/retire',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['ProductCatalog'],
    }),
    searchAsiriVehiclesByVin: builder.mutation<
      IAsiriVehicleSearchResult[],
      IAsiriVehicleSearchRequest
    >({
      query: (body) => ({
        url: '/api/productcatalogs/sap-vins/search',
        method: 'POST',
        body,
      }),
    }),
    syncAsiriVehiclesByVin: builder.mutation<IAsiriVehicleSyncResponse, IAsiriVehicleSyncRequest>({
      query: (body) => ({
        url: '/api/productcatalogs/sap-vins/sync',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ProductCatalog'],
    }),
  }),
});

export { productCatalogApi };

export const {
  useGetProductCatalogQuery,
  useLazyGetProductCatalogsQuery,
  useCreateProductCatalogMutation,
  useUpdateProductCatalogMutation,
  useDeleteProductCatalogMutation,
  useLazyListAttributeTypesQuery,
  useListVehicleTypesQuery,
  useLazyListBrandsQuery,
  useLazyListModelsQuery,
  useLazyGetProductWithSkusQuery,
  useLazyGetVehicleModelsQuery,
  useLazyGetVehicleColorsQuery,
  useLazyGetVehicleYearsQuery,
  useBulkUploadMutation,
  useDownloadProductCatalogTemplateMutation,
  useLazyGetDiscountPriceQuery,
  useListWithdrawReasonsQuery,
  useRetireProductDetailMutation,
  useSearchAsiriVehiclesByVinMutation,
  useSyncAsiriVehiclesByVinMutation,
} = productCatalogApi;
